<template>
  <IdentityShell>
    <template #masthead>
      <IdentityWelcomeMasthead
        kicker="Verified"
        headline="Pick a place to land."
        subtitle="Choose where you want to land. We'll hand you off to that organization's sign-in."
      />
    </template>

    <template #title>You're in. Pick an organization.</template>
    <template #lede>
      These are the Communal organizations linked to
      <strong>{{ email }}</strong
      >. Forgot a password? Use the "Forgot password" link after you click
      through.
    </template>

    <p v-if="!tenants.length" class="directory-results__empty">
      We couldn't find any active organizations for this email. Reach out to
      your admin if you think this is wrong.
    </p>

    <ul v-else class="directory-results__list">
      <li
        v-for="tenant in pagedTenants"
        :key="tenant.url"
        class="directory-results__item"
      >
        <a :href="tenant.url" class="directory-results__link">
          <span class="directory-results__detail">
            <span class="directory-results__name">{{ tenant.name }}</span>
            <span class="directory-results__url">{{
              displayHost(tenant.url)
            }}</span>
          </span>
          <span class="directory-results__chevron" aria-hidden="true">→</span>
        </a>
      </li>
    </ul>

    <div v-if="showPagination" class="directory-results__pagination">
      <p class="directory-results__pagination-summary" aria-live="polite">
        Showing
        <strong>{{ rangeStart }}</strong>
        –
        <strong>{{ rangeEnd }}</strong>
        of
        <strong>{{ tenants.length }}</strong>
        organizations
      </p>
      <PaginationBase
        v-model="currentPage"
        size="sm"
        align="center"
        :total-rows="tenants.length"
        :per-page="perPage"
        aria-label="Organization pages"
      />
    </div>

    <p class="directory-results__back">
      <RouterLink to="/login">Look up another email</RouterLink>
    </p>
  </IdentityShell>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { PaginationBase } from '@/components/base/Pagination';
import {
  IdentityShell,
  IdentityWelcomeMasthead,
} from '@/components/feature/Identity';
import { useIdentityBoot } from '@/composables/useIdentityBoot';

interface DiscoveryTenant {
  name: string;
  url: string;
}

const PER_PAGE = 10;

const boot = useIdentityBoot();

const data = computed(
  () =>
    (boot.data ?? {}) as {
      email?: string;
      tenants?: DiscoveryTenant[];
    }
);

const email = computed(() => data.value.email ?? 'your email');
const tenants = computed<DiscoveryTenant[]>(() => data.value.tenants ?? []);

const perPage = PER_PAGE;
const currentPage = ref(1);

const totalPages = computed(() =>
  Math.max(1, Math.ceil(tenants.value.length / perPage))
);

// If the underlying list shrinks (unlikely, but defensive), clamp the page
// back into range so we never render an empty paginated view.
watch(totalPages, (pages) => {
  if (currentPage.value > pages) {
    currentPage.value = pages;
  }
});

const showPagination = computed(() => tenants.value.length > perPage);

const pagedTenants = computed<DiscoveryTenant[]>(() => {
  if (!showPagination.value) {
    return tenants.value;
  }

  const start = (currentPage.value - 1) * perPage;

  return tenants.value.slice(start, start + perPage);
});

const rangeStart = computed(() =>
  tenants.value.length === 0 ? 0 : (currentPage.value - 1) * perPage + 1
);

const rangeEnd = computed(() =>
  Math.min(currentPage.value * perPage, tenants.value.length)
);

function displayHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
</script>

<style lang="scss" scoped>
$ink: #0f172a;
$muted: #5b6372;
$line: #e6e8ec;
$brand-blue: #1984c4;

.directory-results__empty {
  margin-bottom: 0;
  color: $muted;
}

.directory-results__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.directory-results__link {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1rem;
  border-radius: 0.625rem;
  border: 1px solid $line;
  background: #fff;
  color: $ink;
  text-decoration: none;
  transition:
    border-color 160ms ease,
    background 160ms ease;

  &:hover,
  &:focus-visible {
    border-color: $brand-blue;
    background: #f8fafc;
  }

  &:hover .directory-results__chevron,
  &:focus-visible .directory-results__chevron {
    color: $brand-blue;
    transform: translateX(2px);
  }
}

.directory-results__detail {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1;
  min-width: 0;
}

.directory-results__name {
  font-weight: 600;
  font-size: 1rem;
  color: $ink;
}

.directory-results__url {
  font-size: 0.8125rem;
  color: $muted;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.directory-results__chevron {
  font-size: 1.05rem;
  color: rgba($ink, 0.3);
  transition:
    color 160ms ease,
    transform 160ms ease;
}

.directory-results__pagination {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.25rem;
}

.directory-results__pagination-summary {
  margin: 0;
  font-size: 0.8125rem;
  color: $muted;

  strong {
    color: $ink;
    font-weight: 600;
  }
}

.directory-results__back {
  margin-top: 1.5rem;
  margin-bottom: 0;
  text-align: center;
  font-size: 0.875rem;

  a {
    color: $muted;
    text-decoration: none;

    &:hover {
      color: $brand-blue;
    }
  }
}
</style>
